/**
 * Buildings sim — placement, requirements, construction.
 * Data-driven from buildings_*.json: `cost`, `build_time` (seconds),
 * `footprint` [w, d] metres, `requires: {buildings: [...]}`, `trains: [...]`.
 * Mirrors the UE construction system; rendering lives in render/buildings.ts.
 */
import type { BuildingDef } from "../data/types";

export type BuildingState = "constructing" | "active";

export interface PlacedBuilding {
  uid: number;
  def: BuildingDef;
  x: number;
  z: number;
  state: BuildingState;
  /** 0..1 construction progress. */
  progress: number;
  efficiency: number;
  hp: number;
  maxHp: number;
  /** 0 = player, 1 = enemy. */
  team: 0 | 1;
}

let nextUid = 1;

/** Keep uid generation ahead of restored ids (save/load). */
export function reserveBuildingUid(uid: number): void {
  nextUid = Math.max(nextUid, uid + 1);
}

export function costOf(def: BuildingDef): Record<string, number> {
  return (def.extra["cost"] as Record<string, number>) ?? {};
}

export function buildTimeOf(def: BuildingDef): number {
  return (def.extra["build_time"] as number) ?? 30;
}

export function footprintOf(def: BuildingDef): [number, number] {
  const f = def.extra["footprint"] as [number, number] | undefined;
  return Array.isArray(f) && f.length === 2 ? [f[0], f[1]] : [4, 4];
}

/**
 * Prerequisite buildings and techs that are not yet satisfied.
 *
 * Pass the researched tech ids (ResearchState.completed) to also gate on
 * `requires.techs` from the data (e.g. signal_brazier needs tech_vigiliae).
 * When omitted, only building prerequisites are checked — the AI director
 * path keeps its current behavior until it wires its own research state.
 */
export function missingRequirements(
  def: BuildingDef,
  placed: PlacedBuilding[],
  researched?: ReadonlySet<string>,
): string[] {
  const req = def.extra["requires"] as
    | { buildings?: string[]; techs?: string[] }
    | undefined;
  const active = new Set(
    placed.filter((p) => p.state === "active").map((p) => p.def.id),
  );
  const missingBuildings = (req?.buildings ?? []).filter(
    (id) => !active.has(id),
  );
  const missingTechs =
    researched === undefined
      ? []
      : (req?.techs ?? []).filter((id) => !researched.has(id));
  return [...missingBuildings, ...missingTechs];
}

export function placeBuilding(
  def: BuildingDef,
  x: number,
  z: number,
  instant = false,
  team: 0 | 1 = 0,
  forceUid?: number,
): PlacedBuilding {
  const maxHp = (def.extra["hp"] as number) ?? 800;
  const uid = forceUid ?? nextUid++;
  if (forceUid !== undefined) reserveBuildingUid(forceUid);
  return {
    uid,
    def,
    x,
    z,
    state: instant ? "active" : "constructing",
    progress: instant ? 1 : 0,
    efficiency: 1,
    hp: maxHp,
    maxHp,
    team,
  };
}

/** Advance construction; returns true on the tick it completes. */
export function tickConstruction(b: PlacedBuilding, dt: number): boolean {
  if (b.state === "active") return false;
  b.progress = Math.min(1, b.progress + dt / buildTimeOf(b.def));
  if (b.progress >= 1) {
    b.state = "active";
    return true;
  }
  return false;
}

/** AABB overlap test so buildings can't stack. */
export function overlaps(
  def: BuildingDef,
  x: number,
  z: number,
  placed: PlacedBuilding[],
): boolean {
  const [w, d] = footprintOf(def);
  for (const p of placed) {
    const [pw, pd] = footprintOf(p.def);
    if (
      Math.abs(p.x - x) < (w + pw) / 2 + 0.5 &&
      Math.abs(p.z - z) < (d + pd) / 2 + 0.5
    ) {
      return true;
    }
  }
  return false;
}
